import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { panelSurfaceSx } from './controlCenterUtils';

const periodLabel = (period, granularity, { includeYear = false } = {}) => {
  if (!period) return '';
  const date = new Date(period);
  if (Number.isNaN(date.getTime())) return '';
  if (granularity === 'day') {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
  }
  if (granularity === 'year') {
    return new Intl.DateTimeFormat('en-US', { year: 'numeric' }).format(date);
  }
  // month
  if (includeYear) {
    return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(date);
  }
  return new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date);
};

const seriesSpansMultipleYears = (series) => {
  if (!Array.isArray(series) || series.length < 2) return false;
  const years = new Set();
  series.forEach((row) => {
    const date = new Date(row?.period);
    if (!Number.isNaN(date.getTime())) years.add(date.getFullYear());
  });
  return years.size > 1;
};

export default function CreatedJobsChart({
  data,
  loading,
  error,
  rangeLabel,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const trend = data?.trend;

  const chartData = useMemo(() => {
    const series = trend?.series || [];
    const granularity = trend?.granularity || 'month';
    const includeYear = granularity === 'month' && seriesSpansMultipleYears(series);
    return series.map((row) => ({
      name: periodLabel(row.period, granularity, { includeYear }),
      created: row.created || 0,
      completed: row.completed || 0,
    }));
  }, [trend]);

  return (
    <Box sx={{ ...panelSurfaceSx(theme), height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: { xs: 1.5, sm: 2 },
          pt: { xs: 1.5, sm: 2 },
          pb: 1,
        }}
      >
        <Typography sx={{ fontSize: 15, fontWeight: 500, color: 'text.secondary' }}>
          Created / Completed Jobs
        </Typography>
        {rangeLabel && (
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{rangeLabel}</Typography>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {!loading && !error && (
        <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 2 }}>
          <Box sx={{ display: 'flex', gap: 3, mb: 1.5 }}>
            <Box>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Created</Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
                {trend?.createdTotal ?? 0}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Completed</Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
                {trend?.completedTotal ?? 0}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ width: '100%', height: 240 }}>
            {chartData.length === 0 ? (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No trend data
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="createdFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="completedFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={isDark ? '#334155' : '#e2e8f0'}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                    width={36}
                  />
                  <Tooltip
                    contentStyle={{
                      background: isDark ? '#1e293b' : '#fff',
                      border: `1px solid ${isDark ? '#334155' : '#e7e7e7'}`,
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="created"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fill="url(#createdFill)"
                    name="Created"
                  />
                  <Area
                    type="monotone"
                    dataKey="completed"
                    stroke="#22c55e"
                    strokeWidth={2}
                    fill="url(#completedFill)"
                    name="Completed"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
